import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
	SwipeableDrawer,
	AppBar,
	Toolbar,
	IconButton,
	Grid,
	Box,
	useMediaQuery,
	useTheme,
	Tooltip,
	Slide,
} from "@mui/material";
import { useHideOnScroll } from "../../hooks/useHideOnScroll";
import { useAuth } from "../../contexts/AuthContext";

import Menu from "../sidebar/Menu";
import logo from "../../ressources/logo.png";
import logoBD from "../../ressources/logo_bd.png";
import logoPoultry from "../../ressources/logo_poultry.png";
import logoFoerster from "../../ressources/logo_foerstertag.png";
import brandConfig, { isFoersterTag } from "../../config/brandConfig";
import AnimatedMenuIcon from "../../components/general-components/animated-menu-icon/AnimatedMenuIcon";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import FeedbackIcon from "@mui/icons-material/Feedback";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Alerts from "components/general-components/alerts/Alerts";
import ChatDialog from "components/chatdialog/ChatDialog";
import FeedbackDialog from "components/feedback-dialog/FeedbackDialog";
import { useTranslation } from "react-i18next";

import { HeaderMetricsSettingsProvider } from "../../contexts/HeaderMetricsSettingsContext";
import HeaderMetrics from "../../components/header-metrics/HeaderMetrics";

// Constants for styling to improve maintainability
const HEADER_HEIGHT_DESKTOP = 64;
const HEADER_HEIGHT_MOBILE = 48;
const LOGO_WIDTH_DESKTOP = "120px";
const LOGO_WIDTH_MOBILE = "40px";
const TOUCH_TARGET_MIN = 44;

/**
 * Header component for the application that includes:
 * - Menu drawer toggler
 * - Company logo
 * - Alerts indicator
 * - Logout button
 * - Chat dialog
 */
function HeaderContent({ showDrawer = true }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { logout, userData, currentUser } = useAuth();
	const { t } = useTranslation();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const hideOnScroll = useHideOnScroll({ threshold: 10, scrollThreshold: 100 });
	const previousLocation = useRef(location.pathname);

	// Close drawer on navigation
	useEffect(() => {
		if (location.pathname !== previousLocation.current && drawerOpen) {
			setDrawerOpen(false);
		}
		previousLocation.current = location.pathname;
	}, [location.pathname, drawerOpen]);

	// Get the primary color from CSS variables
	const root = document.documentElement;
	const primaryColor = getComputedStyle(root)
		.getPropertyValue("--primary-main")
		.trim();
	const isDefaultTheme = primaryColor === "#3f51b5";
	const isPoultry =
		userData && typeof userData.Type === "string"
			? userData.Type.toLowerCase() === "poultry"
			: false;

	const handleDrawerToggle = () => {
		setDrawerOpen(!drawerOpen);
	};

        const handleNavigateHome = () => {
                navigate("/");
        };

	async function handleLogout() {
		try {
			await logout();
			navigate("/login", { replace: true });
		} catch (error) {
			console.error("Failed to log out:", error);
			// Consider showing an error notification here
		}
	}

	const headerHeight = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;

	const appBarContent = (
		<AppBar
			position="sticky"
			elevation={3}
			sx={{
				background: "linear-gradient(135deg, var(--main-color) 0%, var(--secondary-color, var(--main-color)) 100%)",
				minHeight: headerHeight,
				height: "auto",
				boxShadow: "var(--shadow-md)",
				borderBottomLeftRadius: "15px",
				borderBottomRightRadius: "15px",
				transition: "min-height 0.2s ease",
			}}
		>
			<Toolbar sx={{
				height: "100%",
				px: isMobile ? 0.5 : 2,
				minHeight: headerHeight,
			}}>
				<Grid container spacing={isMobile ? 0.5 : 1} alignItems="center">
					{/* Menu button */}
					{showDrawer && (
						<Grid item sx={{ position: "relative", zIndex: 10 }}>
							<Tooltip title={drawerOpen ? (t("close-menu") || "Close Menu") : (t("open-menu") || "Open Menu")}>
								<IconButton
									edge="start"
									color="inherit"
									aria-label="menu"
									onClick={handleDrawerToggle}
									sx={{
										color: "var(--primary-contrast)",
										minWidth: TOUCH_TARGET_MIN,
										minHeight: TOUCH_TARGET_MIN,
										padding: isMobile ? "8px" : "12px",
										borderRadius: "8px",
										transition: "background-color 0.2s ease",
										position: "relative",
										zIndex: 10,
										"&:hover": {
											backgroundColor: "rgba(255, 255, 255, 0.2)",
										},
									}}
								>
									<AnimatedMenuIcon isOpen={drawerOpen} />
								</IconButton>
							</Tooltip>
						</Grid>
					)}

					{/* Logo - hidden on mobile */}
					{!isMobile && (
						<Grid item xs>
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								height="100%"
								width="100%"
								sx={{
									position: "absolute",
									left: 0,
									right: 0,
									top: 0,
									bottom: 0,
									pointerEvents: "none",
								}}
							>
								<Box
									component="img"
									src={isPoultry ? logoPoultry : isFoersterTag ? logoFoerster : isDefaultTheme ? logo : logoBD}
									alt="Company Logo"
									width="120"
									height="50"
									loading="eager"
									sx={{
										width: isDefaultTheme || isFoersterTag
											? LOGO_WIDTH_MOBILE
											: LOGO_WIDTH_DESKTOP,
										maxHeight: "50px",
										cursor: "pointer",
										transition: "transform 0.3s ease",
										pointerEvents: "auto",
										"&:hover": {
											transform: "scale(1.1)",
										},
									}}
									onClick={handleNavigateHome}
								/>
							</Box>
						</Grid>
					)}

					{/* Action buttons */}
					<Grid item>
						<Box display="flex" alignItems="center" gap={isMobile ? 0 : 0.5}>
							<Alerts />

							<Tooltip title={t("documentation") || "Documentation"}>
								<IconButton
									aria-label={t("documentation") || "documentation"}
									onClick={() => window.open(process.env.REACT_APP_DOCS_URL || brandConfig.docsUrl, "_blank", "noopener,noreferrer")}
									sx={{
										color: "var(--primary-contrast)",
										minWidth: TOUCH_TARGET_MIN,
										minHeight: TOUCH_TARGET_MIN,
										padding: isMobile ? "8px" : "12px",
										"& .MuiSvgIcon-root": {
											fontSize: isMobile ? "1.25rem" : "1.5rem",
										},
										"&:hover": {
											backgroundColor: "rgba(255, 255, 255, 0.1)",
										},
									}}
								>
									<MenuBookIcon />
								</IconButton>
							</Tooltip>

							{currentUser && (
								<Tooltip title={t("feedback-button-label")}>
									<IconButton
										aria-label={t("feedback-button-label")}
										onClick={() => setFeedbackDialogOpen(true)}
										sx={{
											color: "var(--primary-contrast)",
											minWidth: TOUCH_TARGET_MIN,
											minHeight: TOUCH_TARGET_MIN,
											padding: isMobile ? "8px" : "12px",
											"& .MuiSvgIcon-root": {
												fontSize: isMobile ? "1.25rem" : "1.5rem",
											},
											"&:hover": {
												backgroundColor: "rgba(255, 255, 255, 0.1)",
											},
										}}
									>
										<FeedbackIcon />
									</IconButton>
								</Tooltip>
							)}

							<Tooltip title={t("logout") || "Logout"}>
								<IconButton
									aria-label={t("logout") || "logout"}
									onClick={handleLogout}
									sx={{
										color: "var(--primary-contrast)",
										minWidth: TOUCH_TARGET_MIN,
										minHeight: TOUCH_TARGET_MIN,
										padding: isMobile ? "8px" : "12px",
										"& .MuiSvgIcon-root": {
											fontSize: isMobile ? "1.25rem" : "1.5rem",
										},
										"&:hover": {
											backgroundColor: "rgba(255, 255, 255, 0.1)",
										},
									}}
								>
									<ExitToAppIcon />
								</IconButton>
							</Tooltip>
						</Box>
					</Grid>
				</Grid>
			</Toolbar>
			{/* Metrics Bar */}
			<HeaderMetrics />
		</AppBar>
	);

	return (
		<>
			{/* Wrap AppBar with Slide for mobile hide-on-scroll */}
			{isMobile && showDrawer ? (
				<Slide appear={false} direction="down" in={!hideOnScroll}>
					{appBarContent}
				</Slide>
			) : (
				appBarContent
			)}

			{/* Side menu drawer with swipe support */}
			{showDrawer && (
				<SwipeableDrawer
					anchor="left"
					open={drawerOpen}
					onClose={handleDrawerToggle}
					onOpen={handleDrawerToggle}
					disableSwipeToOpen={false}
					swipeAreaWidth={40}
					hysteresis={0.52}
					minFlingVelocity={400}
					disableDiscovery={false}
					PaperProps={{
						sx: {
							backgroundColor: "var(--background-paper)",
							borderRight: "1px solid var(--divider)",
							width: 300,
						},
					}}
					ModalProps={{
						keepMounted: true,
					}}
					SwipeAreaProps={{
						sx: {
							top: "130px !important",
						},
					}}
					sx={{
						"& .MuiDrawer-paper": {
							touchAction: "pan-y",
						},
					}}
				>
					<Menu onNavigate={() => setDrawerOpen(false)} />
				</SwipeableDrawer>
			)}

			{/* Chat dialog component */}
			<ChatDialog />

			{/* Feedback dialog component */}
			<FeedbackDialog
				open={feedbackDialogOpen}
				onClose={() => setFeedbackDialogOpen(false)}
			/>
		</>
	);
}

export default function Header(props) {
	return (
		<HeaderMetricsSettingsProvider>
			<HeaderContent {...props} />
		</HeaderMetricsSettingsProvider>
	);
}
